import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";

export default function Register() {
  const nav = useNavigate();
  const { register } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "", school: "", role: "teacher" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const ROLES = [
    { id: "teacher", label: "Teacher" },
    { id: "student", label: "Student" },
    { id: "cohort", label: "Educator Cohort" },
  ];

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form);
      toast.success("Account created — welcome!");
      nav("/dashboard");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex items-center justify-center p-8 order-2 lg:order-1">
        <div className="w-full max-w-sm">
          <Link to="/" className="inline-flex items-center mb-10">
            <img src="/logo-light.png" alt="VividVR" className="h-9 w-auto" />
          </Link>
          <h1 className="text-3xl font-display font-bold tracking-tight">Create your account</h1>
          <p className="text-muted-foreground mt-2">Start building immersive lessons for free.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label className="mb-2 block">I am a…</Label>
              <div className="grid grid-cols-3 gap-2">
                {ROLES.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    data-testid={`register-role-${r.id}`}
                    onClick={() => setForm({ ...form, role: r.id })}
                    className={`h-10 rounded-lg border-2 text-xs font-bold transition-colors ${
                      form.role === r.id ? "border-primary bg-primary/5 text-primary" : "border-border text-muted-foreground hover:border-primary/40"
                    }`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label htmlFor="name">Full name</Label>
              <Input id="name" data-testid="register-name-input" value={form.name} onChange={set("name")} className="mt-1.5 h-11" required />
            </div>
            <div>
              <Label htmlFor="school">School (optional)</Label>
              <Input id="school" data-testid="register-school-input" value={form.school} onChange={set("school")} className="mt-1.5 h-11" />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="register-email-input" type="email" value={form.email} onChange={set("email")} className="mt-1.5 h-11" required />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" data-testid="register-password-input" type="password" value={form.password} onChange={set("password")} className="mt-1.5 h-11" required minLength={6} />
            </div>
            <Button type="submit" data-testid="register-submit-btn" disabled={loading} className="w-full h-11 rounded-full text-base">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Create account"}
            </Button>
          </form>

          <p className="text-sm text-muted-foreground mt-6 text-center">
            Already have an account?{" "}
            <Link to="/login" data-testid="register-login-link" className="text-primary font-semibold hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </div>

      <div className="hidden lg:block relative hero-noise order-1 lg:order-2">
        <img
          src="https://images.unsplash.com/photo-1771765856781-3adbac14d75b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHxrMTIlMjBzdHVkZW50cyUyMHZpcnR1YWwlMjByZWFsaXR5JTIwY2xhc3Nyb29tJTIwbGVhcm5pbmd8ZW58MHx8fHwxNzg3NDU5ODI5fDA&ixlib=rb-4.1.0&q=85"
          alt="Teacher assisting students"
          className="absolute inset-0 w-full h-full object-cover mix-blend-luminosity opacity-90"
        />
        <div className="absolute inset-0 bg-gradient-to-tl from-accent/60 to-primary/50" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <h2 className="text-3xl font-display font-bold leading-tight">Every state. Every standard. Every subject.</h2>
        </div>
      </div>
    </div>
  );
}
